// Helper PURO del media plan (sin React), igual que `proposalSummary` y `rowIdentity`.

import {rowIdentity} from './rowIdentity';

/**
 * Qué fila del acordeón queda abierta cuando llega una propuesta.
 *
 * Es la regla que sostiene el "no se pierde el lugar" de la card jiJyztGD, y vive afuera del
 * componente porque es donde se puede romper sin que nadie lo note: son cuatro caminos y sólo uno
 * es el frecuente.
 *
 * @param rows              items del acordeón (ya sin las dudas `NEEDS_DECISION`).
 * @param expandedIdentity  identidad abierta hoy. `undefined` = todavía sin inicializar;
 *                          `null` = el usuario cerró todo (se respeta).
 * @param shape             {index, actionType} de la fila abierta, para reencontrarla si CAMBIÓ de
 *                          forma (al vincular, un CREATE pasa a ser un UPDATE de otra BU).
 * @param sameProposal      true si esta propuesta ya fue auto-abierta antes (no es una regeneración).
 * @param priorityOf        prioridad de "pide acción" de una fila (>0 = accionable).
 * @returns null si no hay que tocar nada, o {identity, shape} con la decisión nueva.
 */
export function resolveExpandedIdentity({rows, expandedIdentity, shape, sameProposal, priorityOf}) {
    const list = rows || [];
    // 1) El camino FRECUENTE: la fila que el usuario venía trabajando sigue existiendo con la misma
    //    identidad. No hay nada que corregir — el render ya la dibujó abierta. Antes había que
    //    reabrirla desde un efecto, y ese ida y vuelta (un frame con el acordeón entero cerrado)
    //    era lo que hacía colapsar el documento y perder el scroll.
    if (expandedIdentity && list.some((i) => rowIdentity(i) === expandedIdentity)) return null;
    // 2) Dentro de la MISMA propuesta se respeta lo que el usuario abrió o cerró, incluido "todo
    //    cerrado". Sin esto, cerrar la última fila abierta la volvería a abrir sola.
    if (sameProposal && expandedIdentity !== undefined) return null;
    // 3) La línea cambió de FORMA: se la reencuentra por posición, exigiendo el mismo tipo de
    //    acción. Sin ese chequeo, una propuesta reordenada abre una fila arbitraria.
    if (expandedIdentity && shape) {
        const byIndex = shape.index < list.length ? list[shape.index] : null;
        if (byIndex && byIndex.action_type === shape.actionType) {
            return {identity: rowIdentity(byIndex),
                    shape: {index: shape.index, actionType: byIndex.action_type}};
        }
    }
    // 4) Propuesta nueva sin foco previo: abrir la 1ra fila que pide acción.
    const best = list.reduce((acc, item, idx) => {
        const p = priorityOf(item);
        return p > 0 && (!acc || p > acc.p) ? {p, idx} : acc;
    }, null);
    return best
        ? {identity: rowIdentity(list[best.idx]),
           shape: {index: best.idx, actionType: list[best.idx].action_type}}
        : {identity: null, shape: null};
}

export default resolveExpandedIdentity;
